import React, { useState, useRef, useEffect } from 'react';
import { api } from '../../utils/api';

interface LangOption { code: string; label: string }

interface Props {
  uiLang: string;                 // language of THIS modal's UI
  defaultTargetLang: string;      // customer's email language
  langOptions: LangOption[];
  emailId?: string;               // reply mode: the email being replied to (instruction optional)
  accountEmail?: string;          // new-mail mode: sending account (picks the firm's KB)
  toEmail?: string;               // new-mail mode: recipient (client profile lookup)
  onInsert: (text: string, lang: string) => void;
  onClose: () => void;
}

// UI strings localized to the 3 supported interface languages
const STRINGS: Record<string, Record<string, string>> = {
  en: {
    title: 'AI Assistant',
    hint: 'Optional: tell me how to reply, in your own words. LEAVE IT EMPTY and I\'ll answer fully automatically from the knowledge base, prices and the customer\'s profile.',
    hintNew: 'Tell me what this email should say — I\'ll write it grounded in the knowledge base and current prices.',
    placeholder: 'Optional — e.g. agree to the meeting but push it to next week… (empty = full auto reply)',
    placeholderNew: 'e.g. Introduce our accounting packages and ask for their company details…',
    autoLang: 'Auto (match the customer)',
    langLabel: 'Reply language',
    generate: 'Generate',
    regenerate: 'Regenerate',
    generating: 'Generating…',
    resultLabel: 'Generated email — review and edit before inserting',
    insert: 'Insert into email',
    cancel: 'Cancel',
    needInput: 'Please describe how you want to reply first.',
    failed: 'Generation failed',
  },
  zh: {
    title: 'AI 助手',
    hint: '可选：用你的话说明怎么回。留空 = 全自动根据知识库、价目表和客户档案回复。',
    hintNew: '告诉我这封邮件要写什么——我会基于知识库和现行价格来写。',
    placeholder: '可选——例如：同意见面但改到下周…（留空 = 全自动回复）',
    placeholderNew: '例如：介绍我们的记账套餐，并询问对方公司资料…',
    autoLang: '自动（跟随客户语言）',
    langLabel: '回复语言',
    generate: '生成',
    regenerate: '重新生成',
    generating: '正在生成…',
    resultLabel: '已生成邮件 — 插入前请检查并可编辑',
    insert: '插入到邮件中',
    cancel: '取消',
    needInput: '请先描述你想如何回复。',
    failed: '生成失败',
  },
  th: {
    title: 'ผู้ช่วย AI',
    hint: 'ไม่บังคับ: บอกแนวทางการตอบด้วยคำพูดของคุณ หรือเว้นว่างไว้ให้ตอบอัตโนมัติจากฐานความรู้ ราคา และโปรไฟล์ลูกค้า',
    hintNew: 'บอกว่าอีเมลนี้ต้องการสื่ออะไร — ฉันจะเขียนโดยอ้างอิงฐานความรู้และราคาปัจจุบัน',
    placeholder: 'ไม่บังคับ — เช่น ตกลงนัดแต่เลื่อนเป็นสัปดาห์หน้า… (เว้นว่าง = ตอบอัตโนมัติ)',
    placeholderNew: 'เช่น แนะนำแพ็กเกจบัญชีของเรา และขอข้อมูลบริษัทของลูกค้า…',
    autoLang: 'อัตโนมัติ (ตามภาษาลูกค้า)',
    langLabel: 'ภาษาที่ใช้ตอบ',
    generate: 'สร้าง',
    regenerate: 'สร้างใหม่',
    generating: 'กำลังสร้าง…',
    resultLabel: 'อีเมลที่สร้างขึ้น — ตรวจสอบและแก้ไขก่อนแทรก',
    insert: 'แทรกลงในอีเมล',
    cancel: 'ยกเลิก',
    needInput: 'กรุณาอธิบายวิธีที่คุณต้องการตอบกลับก่อน',
    failed: 'การสร้างล้มเหลว',
  },
};

export default function AIReplyModal({ uiLang, defaultTargetLang, langOptions, emailId, accountEmail, toEmail, onInsert, onClose }: Props) {
  const s = STRINGS[uiLang] || STRINGS.en;
  const replyMode = !!emailId;
  const [instruction, setInstruction] = useState('');
  const [targetLang, setTargetLang] = useState(replyMode ? 'auto' : defaultTargetLang);
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState('');
  const [resultLang, setResultLang] = useState('');
  const [error, setError] = useState('');
  const taRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => { taRef.current?.focus(); }, []);

  const handleGenerate = async () => {
    if (!replyMode && !instruction.trim()) { setError(s.needInput); return; }
    setError('');
    setGenerating(true);
    try {
      const r: any = await api.kbReply({ emailId, accountEmail, toEmail, instruction: instruction.trim(), targetLang });
      setResult(r.text || '');
      setResultLang(r.lang || '');
    } catch (e: any) {
      setError(s.failed + (e?.message ? ': ' + e.message : ''));
    } finally {
      setGenerating(false);
    }
  };

  const effLang = resultLang || (targetLang === 'auto' ? defaultTargetLang : targetLang);
  const langLabel = langOptions.find(l => l.code === effLang)?.label || effLang;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 5000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
      <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', boxShadow: 'var(--shadow-md)', width: '100%', maxWidth: 560, maxHeight: '92vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <div style={{ padding: '14px 20px', borderBottom: '2px solid var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <i className="ti ti-sparkles" style={{ fontSize: 16, color: 'var(--accent)' }} aria-hidden />
            <span style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)' }}>{s.title}</span>
          </div>
          <button onClick={onClose} style={{ border: 'none', background: 'transparent', color: 'var(--text-tertiary)', cursor: 'pointer', padding: 4 }}>
            <i className="ti ti-x" style={{ fontSize: 16 }} aria-hidden />
          </button>
        </div>

        <div style={{ padding: '16px 20px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Hint */}
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', lineHeight: 1.6, background: 'var(--accent-bg)', padding: '10px 12px', borderRadius: 'var(--radius-md)', display: 'flex', gap: 8 }}>
            <i className="ti ti-bulb" style={{ fontSize: 14, color: 'var(--accent)', flexShrink: 0, marginTop: 1 }} aria-hidden />
            <span>{replyMode ? s.hint : s.hintNew}</span>
          </div>

          {/* Instruction input */}
          <textarea
            ref={taRef}
            value={instruction}
            onChange={e => setInstruction(e.target.value)}
            placeholder={replyMode ? s.placeholder : s.placeholderNew}
            rows={4}
            style={{ width: '100%', resize: 'vertical', padding: '10px 12px', fontSize: 13, lineHeight: 1.5, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none', fontFamily: 'inherit' }}
          />

          {/* Language selector + generate */}
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 }}>{s.langLabel}</label>
              <select value={targetLang} onChange={e => setTargetLang(e.target.value)}
                style={{ width: '100%', padding: '7px 10px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none', cursor: 'pointer' }}>
                {replyMode && <option value="auto">{s.autoLang}</option>}
                {langOptions.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </div>
            <button onClick={handleGenerate} disabled={generating}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px', background: 'var(--accent)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 13, fontWeight: 600, cursor: generating ? 'not-allowed' : 'pointer', opacity: generating ? 0.7 : 1 }}>
              {generating
                ? <><i className="ti ti-loader-2" style={{ fontSize: 14, animation: 'spin 1s linear infinite' }} aria-hidden />{s.generating}</>
                : <><i className="ti ti-sparkles" style={{ fontSize: 14 }} aria-hidden />{result ? s.regenerate : s.generate}</>
              }
            </button>
          </div>

          {error && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)' }}>{error}</div>}

          {/* Result (editable) */}
          {result && (
            <div>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-tertiary)', marginBottom: 6, display: 'flex', alignItems: 'center', gap: 6 }}>
                <i className="ti ti-language" style={{ fontSize: 12, color: 'var(--accent)' }} aria-hidden />
                {s.resultLabel} · <span style={{ color: 'var(--accent)' }}>{langLabel}</span>
              </div>
              <textarea
                value={result}
                onChange={e => setResult(e.target.value)}
                rows={9}
                style={{ width: '100%', resize: 'vertical', padding: '10px 12px', fontSize: 13, lineHeight: 1.6, borderRadius: 'var(--radius-md)', border: '1px solid color-mix(in srgb, var(--accent) 35%, transparent)', background: '#fff', color: 'var(--text-primary)', outline: 'none', fontFamily: 'inherit' }}
              />
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0 }}>
          <button onClick={onClose} style={{ padding: '8px 16px', fontSize: 13, borderRadius: 'var(--radius-md)', border: '1px solid var(--border)', background: 'transparent', color: 'var(--text-secondary)', cursor: 'pointer' }}>{s.cancel}</button>
          <button onClick={() => result.trim() && onInsert(result.trim(), resultLang || (targetLang === 'auto' ? defaultTargetLang : targetLang))} disabled={!result.trim()}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px', background: result.trim() ? 'var(--accent)' : 'var(--text-tertiary)', color: '#fff', border: 'none', borderRadius: 'var(--radius-md)', fontSize: 13, fontWeight: 600, cursor: result.trim() ? 'pointer' : 'not-allowed' }}>
            <i className="ti ti-check" style={{ fontSize: 14 }} aria-hidden />{s.insert}
          </button>
        </div>
      </div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
